<template>
  <ContentCard :loading="loadingUpdateAgency">
    <template #header>
      <ReviewsAgencyTabs />
    </template>
    <template #subheader>
      <ContentCardSubheader :back-to="{ name: ReviewsRouteName.ReviewsAgenciesIndex }">
        {{ t('Reviews.agency.edit.traitsTitle') }}
        <template #actions>
          <button class="btn-secondary-color-s mr-2" data-testid="agency-traits-cancel-button" @click="onCancel">
            {{ t('Reviews.edit.action.cancel') }}
          </button>
          <button class="btn-primary-color-s" data-testid="save-button" :disabled="traitsFenced" @click="onSubmit">
            {{ t('Reviews.edit.action.save') }}
          </button>
        </template>
      </ContentCardSubheader>
    </template>
    <div class="pt-6 pb-12">
      <div class="mt-6 flex text-main">
        <div class="text-headline-xs">{{ t('Reviews.agency.edit.traits.title') }}</div>
      </div>
      <div class="flex pt-2 text-body-s">
        <MdiSvg class="mr-1" :path="mdiInformationOutline" />
        {{ t('Reviews.agency.edit.traits.description') }}
      </div>
    </div>
    <FeatureFencingWrapper
      :fenced="traitsFenced"
      :label="t('Reviews.agency.view.traitsEdit.fencingLabel')"
      :help-tooltip="t('Reviews.agency.view.traitsEdit.fencingTooltip')"
      :help-link="t('Reviews.agency.view.traitsEdit.fencingHelpLink')"
      :upgrade-cta="t('Reviews.agency.view.traitsEdit.fencingUpgradeCta')"
      :upgrade-cta-link="t('Reviews.agency.view.traitsEdit.fencingUpgradeCtaLink')"
      placement="traits"
      entity-type="service"
      :entity-id="agency.id"
    >
      <div class="grid grid-cols-2 gap-6 pb-12">
        <div :class="{ 'text-disabled': traitsFenced }">
          <TraitForm
            v-for="(trait, index) in traits"
            :key="trait._vid"
            :trait="trait"
            :index="index"
            :total="traits.length - 1"
            :disabled="traitsFenced"
            @move-up="onMoveUp"
            @move-down="onMoveDown"
            @delete="onDelete"
          />
          <div class="justify-self-end">
            <button
              class="btn-primary-color-s mt-2 justify-self-end rounded-sm"
              data-testid="agency-traits-add-button"
              :disabled="traitsFenced || countNotDestroyedTraits >= MAX_TRAITS"
              @click.prevent="addTraits"
            >
              <MdiSvg :path="mdiPlusCircleOutline" class="mr-2" />
              {{ t('Reviews.agency.edit.traits.form.add') }}
            </button>
          </div>
        </div>
        <div v-if="!traitsFenced">
          <div class="rounded-sm bg-surface-elevation-4 p-4">
            <div v-for="lang in ALLOWED_LOCALES" :key="lang" class="pb-8">
              <ChipBadge rounded color="warning">{{ t('Reviews.agency.edit.traits.form.preview') }} {{ lang.toUpperCase() }}</ChipBadge>
              <div class="mt-4 rounded-sm bg-surface p-4">
                <TraitsList :traits="traits" :lang="lang" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </FeatureFencingWrapper>
    <ModalDialog ref="deleteModal" data-testid="agency-traits-delete-modal">
      <template #header>
        <div class="flex items-center justify-between px-4 pt-4 pb-2">
          <div class="flex items-center text-body-l-bold capitalize">
            <MdiSvg :size="24" :path="mdiTrashCanOutline" class="mr-1 text-error" />
            <span v-if="deleteKind === 'usp'">
              {{ t('Reviews.agency.edit.traits.form.deleteUsp.title') }}
            </span>
            <span v-else>
              {{ t('Reviews.agency.edit.traits.form.deleteCert.title') }}
            </span>
          </div>
          <div class="cursor-pointer">
            <button class="btn-link-neutral-m" data-testid="agency-traits-del-close" @click="onCancelDelete">
              <MdiSvg :path="mdiClose" size="24" />
            </button>
          </div>
        </div>
      </template>
      <div v-if="deleteKind === 'usp'">
        {{ t('Reviews.agency.edit.traits.form.deleteUsp.description') }}
      </div>
      <div v-else>
        {{ t('Reviews.agency.edit.traits.form.deleteCert.description') }}
      </div>
      <div class="place-self-end pt-4">
        <button class="btn-secondary-color-s mr-2" data-testid="agency-traits-del-cancel" @click="onCancelDelete">
          {{ t('Reviews.edit.action.cancel') }}
        </button>
        <button class="btn-primary-color-s" data-testid="agency-traits-del-confirm" @click="onConfirmDelete">
          {{ t('Reviews.edit.action.delete') }}
        </button>
      </div>
    </ModalDialog>
    <EditCloseConfirmModal ref="confirmCloseModal" :back-to="backTo" />
  </ContentCard>
</template>
<script setup lang="ts">
import { mdiInformationOutline, mdiPlusCircleOutline, mdiClose, mdiTrashCanOutline } from '@mdi/js'
import { MdiSvg, ChipBadge, ModalDialog } from '@ramp106/omrjs-core-ui'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { useVuelidate } from '@vuelidate/core'
import { v4 as uuidv4 } from 'uuid'
import { computed, inject, reactive, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import FeatureFencingWrapper from '@/components/ReviewsCommon/Fencing/FeatureFencingWrapper.vue'
import EditCloseConfirmModal from '@/components/ReviewsManagement/EditCloseConfirmModal.vue'
import { AgencyTraitKindEnum, useUpdateReviewsAgencyMutation, type AgencyData, type ManageAgencyAttributes } from '@/gql/reviews'
import aggregateResponseErrors from '@/helpers/aggregateResponseErrors'
import { showNotification } from '@/helpers/notificationHelper'
import { ALLOWED_LOCALES, mapTranslationsArrToObj } from '@/helpers/translationsHelper'
import router from '@/router'
import { ReviewsRouteName } from '@/router/types'
import { reloadReviewsAgencyKey } from '@/symbols/reviewsReloadKeys'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'

import ReviewsAgencyTabs from '../ReviewsAgencyTabs.vue'
import TraitForm from './TraitForm.vue'
import TraitsList from './TraitsList.vue'

const props = defineProps<{
  agency: AgencyData
}>()

const traitsFenced = computed(() => !props.agency.configFlags.traitsEditable)

const backTo = { name: ReviewsRouteName.ReviewsAgencyViewTraits, params: { agencyId: props.agency.id } }

const initialTraitsOrder: Record<string, number> = {}

const traits = reactive(
  props.agency.traits
    .map((trait) => {
      initialTraitsOrder[trait.id] = trait.position
      return {
        _vid: uuidv4(),
        id: trait.id,
        position: trait.position,
        kind: trait.kind,
        title: trait.titleTranslations ? mapTranslationsArrToObj(trait.titleTranslations) : {},
        description: trait.descriptionTranslations ? mapTranslationsArrToObj(trait.descriptionTranslations) : {},
        _destroy: false,
      }
    })
    .sort((a, b) => a.position - b.position),
)

const countNotDestroyedTraits = computed(() => {
  return traits.filter((trait) => !trait._destroy).length
})

if (traits.length === 0) {
  addTraits()
}

const traitsBeenReorderd = computed(() => {
  let reorderd = false
  traits.forEach((trait) => {
    if (initialTraitsOrder[trait.id] !== trait.position) {
      reorderd = true
    }
  })
  return reorderd
})

const MAX_TRAITS = 6

const { t } = useI18n()
const v$ = useVuelidate()

const deleteModalRef = useTemplateRef('deleteModal')
const confirmCloseModal = useTemplateRef('confirmCloseModal')

let deleteKind: string
let deleteIndex: number | undefined

const reloadReviewsAgency = inject(reloadReviewsAgencyKey) as () => void

const {
  mutate: mutateAgency,
  loading: loadingUpdateAgency,
  onError,
} = useUpdateReviewsAgencyMutation({
  clientId: 'reviews',
})

onError((error) => showError(error))
const showError = (error: Error) => {
  if (error) showNotification(error.message, 'error')
}

async function onSubmit() {
  track('profile_update', { surface: 'editor', entity_type: 'service', entity_id: props.agency.id })
  let lastItemIsEmpty = true
  traits
    .filter((trait) => !trait._destroy)
    .forEach((trait) => {
      if (trait.title.de !== '' || trait.title.en !== '' || trait.description.de !== '' || trait.description.en !== '') {
        lastItemIsEmpty = false
        return
      }
    })
  if (!lastItemIsEmpty) {
    v$.value.$touch()
    if (v$.value.$invalid) {
      showNotification(t('Reviews.agency.notifications.invalidData'), 'error')
      return
    }
  } else {
    traits.pop()
  }

  try {
    const traitsMappedForQuery = traits.map((trait) => {
      return {
        id: trait.id,
        kind: trait.kind,
        position: trait.position,
        _destroy: trait._destroy,
        titleTranslations: Object.entries(trait.title).map(([locale, value]) => ({
          locale,
          value,
        })),
        descriptionTranslations: Object.entries(trait.description).map(([locale, value]) => ({
          locale,
          value,
        })),
      }
    })

    const attributes: ManageAgencyAttributes = {
      traitsAttributes: traitsMappedForQuery,
    }

    const mutationResponse = await mutateAgency({
      id: props.agency.id,
      attributes,
    })
    /* TODO: this is a temporary solution. We should avoid having nested errors in the future.
        And align backend and frontend better */
    const aggregatedResponseErrors = aggregateResponseErrors(mutationResponse?.data?.manage.updateAgency)
    if (aggregatedResponseErrors.length > 0) {
      let outputString = ''
      aggregatedResponseErrors.forEach((err) => {
        outputString += t('Reviews.agency.notifications.errorTemplate', {
          field: err.field,
          value: err.value,
          fieldPath: err.fieldPath,
          message: err.message,
        })
      })
      showNotification(outputString, 'error')
    } else {
      reloadReviewsAgency()
      showNotification(t('Reviews.agency.notifications.savedSuccessfully', { name: props.agency.title }), 'success')
      router.push(backTo)
    }
  } catch {
    showNotification(t('Reviews.agency.notifications.invalidData'), 'error')
  }
}

function onCancel() {
  track('profile_update_cancel', { surface: 'editor', entity_type: 'service', entity_id: props.agency.id })
  if (v$.value.$anyDirty || traitsBeenReorderd.value) {
    confirmCloseModal.value?.show()
  } else {
    router.push(backTo)
  }
}

function onMoveDown(index: number) {
  traits[index].position += 1
  traits[index + 1].position -= 1
}

function onMoveUp(index: number) {
  traits[index - 1].position += 1
  traits[index].position -= 1
}

function onDelete(index: number) {
  deleteIndex = index
  deleteKind = traits[deleteIndex].kind
  deleteModalRef.value?.open()
}

function onCancelDelete() {
  deleteIndex = undefined
  deleteModalRef.value?.close()
}

function onConfirmDelete() {
  if (deleteIndex != undefined) {
    if (traits[deleteIndex].id === '') {
      traits.splice(deleteIndex, 1)
    } else {
      traits[deleteIndex]._destroy = true
    }
    let remainingTraitsCounter = 0
    traits.forEach((trait) => {
      if (trait._destroy === true) {
        trait.position = 0
      } else {
        remainingTraitsCounter++
        trait.position = remainingTraitsCounter
      }
    })
  }
  if (countNotDestroyedTraits.value === 0) {
    addTraits()
  }
  deleteModalRef.value?.close()
  showNotification(t('Reviews.agency.notifications.deletedSuccessfully', { name: props.agency.title }), 'success')
}

function addTraits() {
  traits.push({
    _vid: uuidv4(),
    id: '',
    description: ALLOWED_LOCALES.reduce((acc, locale) => ({ ...acc, [locale]: '' }), {}),
    title: ALLOWED_LOCALES.reduce((acc, locale) => ({ ...acc, [locale]: '' }), {}),
    kind: AgencyTraitKindEnum.Certificate,
    position: countNotDestroyedTraits.value === 0 ? 1 : traits.length + 1,
    _destroy: false,
  })
}

watch(
  () => traits,
  () => {
    traits.sort((a, b) => a.position - b.position)
  },
  { deep: true },
)
</script>
